import { useEffect, useState, useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Target, Eye, Lightbulb, Users } from "lucide-react";
import axios from "axios";
import { resolveImageUrl } from "../utils/image";
import SEO from "../components/SEO";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DEFAULT_HERO_IMAGE = "https://images.unsplash.com/photo-1765344550212-a3b963d63c32?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHwxfHxjaW5lbWF0aWMlMjB2aWRlbyUyMHByb2R1Y3Rpb24lMjBiZWhpbmQlMjB0aGUlMjBzY2VuZXMlMjBjYW1lcmElMjBjcmV3fGVufDB8fHx8MTc3MTYyMDgyMHww&ixlib=rb-4.1.0&q=85";
const DEFAULT_FOUNDER_IMAGE = "https://images.unsplash.com/photo-1761499930744-1d689014c9a6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHw0fHxjaW5lbWF0aWMlMjB2aWRlbyUyMHByb2R1Y3Rpb24lMjBiZWhpbmQlMjB0aGUlMjBzY2VuZXMlMjBjYW1lcmElMjBjcmV3fGVufDB8fHx8MTc3MTYyMDgyMHww&ixlib=rb-4.1.0&q=85";

const DEFAULTS = {
  about_hero_label: "About Us",
  about_hero_headline: "More Than Content. We Build Brand Presence.",
  about_hero_desc: "OBRA Creatives exists to bridge the gap between creative excellence and business results. We believe powerful visuals should drive measurable outcomes.",
  about_story_label: "Our Story",
  about_story_title: "Born from Passion, Driven by Purpose",
  about_story_p1: "OBRA Creatives emerged from years of experience in premium visual storytelling. As the commercial division of an established creative studio, we recognized that businesses needed more than beautiful content—they needed strategic visual assets that drive real results.",
  about_story_p2: "Based in Manawatu, New Zealand, we've had the privilege of working with brands across industries—from luxury fashion and architecture to technology startups and established corporations.",
  about_story_p3: "Our approach is simple: understand your objectives, craft compelling visuals, and deliver measurable impact. We're not just content creators—we're strategic partners invested in your success.",
  about_philosophy_label: "Our Philosophy",
  about_philosophy_title: "Storytelling Meets Strategy",
  about_value_1_title: "Strategic Focus",
  about_value_1_desc: "Every visual decision is guided by your business objectives. We don't just create content—we solve problems.",
  about_value_2_title: "Cinematic Quality",
  about_value_2_desc: "We bring film-industry standards to commercial work. Premium visuals that command attention and respect.",
  about_value_3_title: "Creative Innovation",
  about_value_3_desc: "We push boundaries while staying true to your brand. Fresh perspectives that differentiate you from competitors.",
  about_value_4_title: "True Partnership",
  about_value_4_desc: "We become an extension of your team. Long-term relationships built on trust, communication, and results.",
  about_founder_label: "The Founder",
  about_founder_title: "Vision Meets Execution",
  about_founder_p1: "With over a decade of experience in visual storytelling, our founder brings a unique perspective that bridges artistic vision with business acumen.",
  about_founder_p2: "Having worked with brands ranging from startups to multinational corporations, the experience spans commercial photography, cinematic videography, and comprehensive brand campaigns.",
  about_founder_quote: "Every brand has a story worth telling. Our job is to find that story, craft it with excellence, and present it in a way that moves people to action.",
  about_cta_title: "Ready to Work Together?",
  about_cta_desc: "Let's discuss how we can help elevate your brand through powerful visual content.",
  about_cta_btn: "Start a Conversation",
};

const valueIcons = [Target, Eye, Lightbulb, Users];

// ── Staggered word reveal ──
function AnimatedHeadline({ text, className, delay = 0 }) {
  const words = text.split(" ");
  return (
    <motion.h1 className={className}>
      {words.map((word, i) => (
        <span key={i} className="inline-block overflow-hidden mr-[0.3em]">
          <motion.span
            className="inline-block"
            initial={{ y: "110%", rotateX: -80 }}
            animate={{ y: 0, rotateX: 0 }}
            transition={{
              duration: 0.7,
              delay: delay + i * 0.06,
              ease: [0.22, 1, 0.36, 1],
            }}
          >
            {word}
          </motion.span>
        </span>
      ))}
    </motion.h1>
  );
}

// ── Parallax image ──
function ParallaxImage({ src, alt, className }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);

  return (
    <div ref={ref} className={`overflow-hidden ${className || ""}`}>
      <motion.img
        src={src}
        alt={alt}
        style={{ y }}
        className="w-full h-[116%] object-cover"
      />
    </div>
  );
}

// ── Scroll reveal wrapper ──
function Reveal({ children, delay = 0, direction = "up" }) {
  const variants = {
    hidden: {
      opacity: 0,
      y: direction === "up" ? 40 : direction === "down" ? -40 : 0,
      x: direction === "left" ? 40 : direction === "right" ? -40 : 0,
    },
    visible: { opacity: 1, y: 0, x: 0 },
  };
  return (
    <motion.div
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: "-80px" }}
      variants={variants}
      transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.div>
  );
}

export default function About() {
  const [heroImage, setHeroImage] = useState(DEFAULT_HERO_IMAGE);
  const [founderImage, setFounderImage] = useState(DEFAULT_FOUNDER_IMAGE);
  const [content, setContent] = useState({});

  const c = (key) => content[key] || DEFAULTS[key] || "";

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/site-settings`),
      axios.get(`${API}/site-content`)
    ]).then(([settingsRes, contentRes]) => {
      if (settingsRes.data.about_hero_image) setHeroImage(settingsRes.data.about_hero_image);
      if (settingsRes.data.founder_image) setFounderImage(settingsRes.data.founder_image);
      setContent(contentRes.data || {});
    }).catch(() => {});
  }, []);

  return (
    <div data-testid="about-page" className="pt-40">
      <SEO title="About" description="Meet OBRA Creatives, a commercial photographer and videographer based in Manawatū, creating cinematic brand content for businesses across Palmerston North and Wellington." path="/about" />

      {/* Hero */}
      <section className="py-24 md:py-32 bg-[#050505]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <motion.span initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
                className="text-[#D4C5B0] text-sm uppercase tracking-[0.3em] font-medium block mb-4">
                {c("about_hero_label")}
              </motion.span>
              <AnimatedHeadline text={c("about_hero_headline")} className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#EDEDED] mb-6 leading-tight" delay={0.2} />
              <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.8 }}
                className="text-[#A3A3A3] text-lg leading-relaxed">
                {c("about_hero_desc")}
              </motion.p>
            </div>
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8, delay: 0.3 }} className="relative">
              <ParallaxImage src={resolveImageUrl(heroImage)} alt="OBRA Creatives Studio" className="aspect-square" />
              <motion.div initial={{ scaleX: 0, scaleY: 0 }} animate={{ scaleX: 1, scaleY: 1 }} transition={{ duration: 0.8, delay: 0.6 }}
                className="absolute -bottom-6 -left-6 w-32 h-32 border border-[#D4C5B0] -z-10 origin-bottom-left" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Founder — The Person Behind The Lens */}
      <section data-testid="founder-section" className="py-24 md:py-32 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <Reveal direction="left">
              <div className="order-2 lg:order-1">
                <span className="text-[#D4C5B0] text-sm uppercase tracking-[0.3em] font-medium">{c("about_founder_label")}</span>
                <h2 className="text-3xl md:text-4xl font-bold text-[#EDEDED] mt-4 mb-6">{c("about_founder_title")}</h2>
                <p className="text-[#A3A3A3] text-lg leading-relaxed mb-6">{c("about_founder_p1")}</p>
                <p className="text-[#A3A3A3] leading-relaxed mb-6">{c("about_founder_p2")}</p>
                <p className="text-[#A3A3A3] leading-relaxed italic">"{c("about_founder_quote")}"</p>
              </div>
            </Reveal>
            <Reveal direction="right" delay={0.15}>
              <div className="order-1 lg:order-2">
                <ParallaxImage src={resolveImageUrl(founderImage)} alt="Founder Portrait" className="aspect-[3/4]" />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Story */}
      <section data-testid="story-section" className="py-24 md:py-32 bg-[#050505]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-3xl mx-auto text-center">
            <Reveal>
              <span className="text-[#D4C5B0] text-sm uppercase tracking-[0.3em] font-medium">{c("about_story_label")}</span>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#EDEDED] mt-4 mb-8">{c("about_story_title")}</h2>
            </Reveal>
            <Reveal delay={0.15}>
              <div className="space-y-6 text-left">
                <p className="text-[#A3A3A3] text-lg leading-relaxed">{c("about_story_p1")}</p>
                <p className="text-[#A3A3A3] leading-relaxed">{c("about_story_p2")}</p>
                <p className="text-[#A3A3A3] leading-relaxed">{c("about_story_p3")}</p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Philosophy / Values */}
      <section data-testid="philosophy-section" className="py-24 md:py-32 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <div className="text-center mb-16">
              <span className="text-[#D4C5B0] text-sm uppercase tracking-[0.3em] font-medium">{c("about_philosophy_label")}</span>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#EDEDED] mt-4">{c("about_philosophy_title")}</h2>
            </div>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {[1, 2, 3, 4].map((i, index) => {
              const Icon = valueIcons[index];
              return (
                <Reveal key={i} delay={index * 0.1}>
                  <motion.div whileHover={{ y: -6, borderColor: "#D4C5B0" }} transition={{ duration: 0.3 }}
                    data-testid={`value-card-${index}`} className="bg-[#121212] border border-[#262626] p-8 group">
                    <Icon className="w-10 h-10 text-[#D4C5B0] mb-6 transition-transform duration-500 group-hover:scale-110 group-hover:rotate-[-6deg]" />
                    <h3 className="text-xl font-bold text-[#EDEDED] mb-4">{c(`about_value_${i}_title`)}</h3>
                    <p className="text-[#A3A3A3] leading-relaxed">{c(`about_value_${i}_desc`)}</p>
                  </motion.div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 bg-[#050505] border-t border-[#262626]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <Reveal>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#EDEDED] mb-6">{c("about_cta_title")}</h2>
            <p className="text-[#A3A3A3] text-lg mb-10 max-w-2xl mx-auto">{c("about_cta_desc")}</p>
            <Link to="/contact" data-testid="about-cta-button" className="inline-flex items-center gap-2 btn-primary text-sm uppercase tracking-wider">
              {c("about_cta_btn")} <ArrowRight size={18} />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
